<template>
  <li
    ref="groupRef"
    class="app-sidebar__group"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave">
    <div class="app-sidebar__group-header">
      <AppLink
        :href="item.href"
        class="app-sidebar__item"
        :class="{ active: item.active }">
        <i
          v-if="item.icon"
          :class="item.icon" />
        <span>{{ item.label }}</span>
      </AppLink>
      <button
        type="button"
        class="app-sidebar__group-toggle"
        :class="{ 'app-sidebar__group-toggle--collapsed': !isOpen }"
        :aria-expanded="isOpen"
        :aria-label="`${isOpen ? 'Collapse' : 'Expand'} ${item.label} submenu`"
        @click="onToggleClick">
        <i class="fa-solid fa-chevron-down" />
      </button>
    </div>

    <ul
      class="app-sidebar__children"
      :class="{ 'app-sidebar__children--collapsed': !item.expanded }">
      <li
        v-for="child in item.children"
        :key="child.href">
        <AppLink
          :href="child.href"
          class="app-sidebar__item"
          :class="{ active: child.active }">
          <i
            v-if="child.icon"
            :class="child.icon" />
          <span>{{ child.label }}</span>
        </AppLink>
      </li>
    </ul>

    <FlyoutContent>
      <div
        class="app-sidebar-flyout"
        @mouseenter="cancelScheduledClose"
        @mouseleave="onMouseLeave">
        <AppLink
          :href="item.href"
          class="app-sidebar-flyout__title"
          @click="closeFlyout">
          {{ item.label }}
        </AppLink>
        <ul class="app-sidebar-flyout__list">
          <li
            v-for="child in item.children"
            :key="child.href">
            <AppLink
              :href="child.href"
              class="app-sidebar-flyout__item"
              :class="{ 'app-sidebar-flyout__item--active': child.active }"
              @click="closeFlyout">
              <i
                v-if="child.icon"
                :class="child.icon" />
              <span>{{ child.label }}</span>
            </AppLink>
          </li>
        </ul>
      </div>
    </FlyoutContent>
  </li>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import useDropdown from '@/composables/dropdown'

const props = defineProps({
  item: { type: Object, required: true },
  compact: { type: Boolean, default: false }
})

const emit = defineEmits(['toggle'])

const groupRef = ref(null)
const {
  DropdownContent: FlyoutContent, isOpen: flyoutOpen, toggle: toggleFlyout, open: openFlyout, close: closeFlyout
} = useDropdown(groupRef, { placement: 'right-start', offset: 8 })

// full mode opens/collapses the inline children (shared store state),
// compact mode opens/closes the flyout (local state)
const isOpen = computed(() => (props.compact ? flyoutOpen.value : Boolean(props.item.expanded)))

let closeTimer = null

// hover-opening is an enhancement for pointer devices only — on touch the
// chevron click is the sole trigger (a tap emulates mouseenter/mouseleave,
// which without this guard would fight the click)
const canHover = () => Boolean(window.matchMedia?.('(hover: hover)').matches)

function onToggleClick() {
  if (!props.compact) {
    emit('toggle')
    return
  }

  cancelScheduledClose()
  toggleFlyout()
}

function onMouseEnter() {
  if (!props.compact || !canHover()) return

  cancelScheduledClose()
  openFlyout()
}

// the flyout is teleported to <body>, so moving the pointer from the item
// into the panel briefly leaves both — close on a short delay that the
// panel's own mouseenter cancels
function onMouseLeave() {
  if (!flyoutOpen.value || !canHover()) return

  cancelScheduledClose()
  closeTimer = setTimeout(closeFlyout, 150)
}

function cancelScheduledClose() {
  if (!closeTimer) return

  clearTimeout(closeTimer)
  closeTimer = null
}

watch(() => props.compact, (compact) => {
  if (!compact) closeFlyout()
})

onBeforeUnmount(cancelScheduledClose)
</script>
